import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, ArrowLeft, Plus, Trash2, ChevronLeft, ChevronRight,
  List, Grid3X3, X, ArrowUpRight, FileText, Loader2, Edit2,
  GripVertical, Sparkles, Wand2, Undo2,
  Clock, Download, Printer, Save,
  User, Check, CheckCircle2, XCircle, AlertCircle, Import, Copy, Share2, Edit, Receipt, RotateCcw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_SLOTS = [
  '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00'
];

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show: 'bg-amber-100 text-amber-700'
};

// Draggable Entry Component
function DraggableEntry({ entry, dayDate, onStatusChange, onDelete, onEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `${dayDate}-${entry.id}`,
    data: { entry, dayDate }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none',  // critical for mobile drag (prevents page scroll)
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`px-3 sm:px-4 py-2.5 hover:bg-stone-50 ${isDragging ? 'bg-blue-50 shadow-lg ring-2 ring-blue-300 rounded-lg' : ''}`}
    >
      {/* Top row: drag handle, time, name, status */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-stone-100 rounded touch-none shrink-0"
          title="Drag to reschedule"
        >
          <GripVertical className="w-3.5 h-3.5 text-stone-400" />
        </button>
        
        {entry.time_slot && (
          <span className="text-xs text-stone-500 shrink-0">{entry.time_slot}{entry.duration && entry.duration !== 30 ? ` (${entry.duration}m)` : ''}</span>
        )}
        <p className="font-medium text-primary-900 text-sm truncate min-w-0 flex-1">{entry.child_name}</p>
        <Select
          value={entry.status}
          onValueChange={(v) => onStatusChange(dayDate, entry.id, v)}
        >
          <SelectTrigger className={`h-6 w-[90px] rounded-full text-[10px] shrink-0 ${STATUS_COLORS[entry.status]}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="no_show">No Show</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => onEdit(dayDate, entry)}
          className="p-1.5 text-stone-400 hover:text-blue-500 shrink-0"
          title="Edit"
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(dayDate, entry.id)}
          className="p-1.5 text-stone-400 hover:text-red-500 shrink-0"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Bottom row: badges (only if present) */}
      {(entry.treatment_code || entry.diagnostic_code || entry.session_note_id) && (
        <div className="flex items-center gap-1.5 mt-1 ml-7 flex-wrap">
          {entry.treatment_code && entry.treatment_code.split(',').map(c => c.trim()).filter(Boolean).map((code, i) => (
            <Badge key={`t-${i}`} variant="outline" className="text-[10px] py-0 px-1.5 h-4">T: {code}</Badge>
          ))}
          {entry.diagnostic_code && entry.diagnostic_code.split(',').map(c => c.trim()).filter(Boolean).map((code, i) => (
            <Badge key={`d-${i}`} variant="outline" className="text-[10px] py-0 px-1.5 h-4">D: {code}</Badge>
          ))}
          {entry.session_note_id && (
            <Badge className="text-[10px] py-0 px-1.5 h-4 bg-blue-100 text-blue-700">SOAP</Badge>
          )}
        </div>
      )}
    </div>
  );
}

// Draggable entry in the timetable (grid) view — same dnd-kit id format as
// the list view's DraggableEntry so handleDragEnd can treat them
// interchangeably.
function GridDraggableEntry({ entry, dayDate, slot, onEdit }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: `${dayDate}-${entry.id}`,
    data: { entry, dayDate },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none',  // critical for mobile drag (prevents scroll)
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Suppress click during drag — only treat a true non-drag click as edit.
        if (isDragging) { e.stopPropagation(); return; }
        onEdit?.();
      }}
      className={`px-2 py-1 rounded text-xs truncate cursor-grab active:cursor-grabbing ${STATUS_COLORS[entry.status]}`}
      title={`${entry.child_name}${entry.time_slot && entry.time_slot !== slot ? ` (${entry.time_slot})` : ''} — drag to move`}
      data-testid={`grid-entry-${entry.id}`}
    >
      {entry.child_name}{entry.time_slot && entry.time_slot !== slot ? ` ${entry.time_slot}` : ''}
    </div>
  );
}

// Droppable Day Zone
function DroppableDay({ date, children }) {
  const { isOver, setNodeRef } = useDroppable({
    id: date,
  });

  return (
    <div 
      ref={setNodeRef}
      className={`min-h-[60px] transition-colors ${isOver ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
    >
      {children}
    </div>
  );
}

// Droppable time-slot cell — used in the timetable (grid) view AND in the
// list view so a therapist can land an entry on a specific time. The id
// format is `slot|<date>|<time>` so handleDragEnd can pull date + time out.
function DroppableTimeCell({ date, time, children, className = '', as: As = 'div' }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `slot|${date}|${time}`,
    data: { date, time },
  });
  return (
    <As
      ref={setNodeRef}
      className={`${className} transition-colors ${isOver ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
    >
      {children}
    </As>
  );
}

export default function AdminDaysheet({ onLogout }) {
  const navigate = useNavigate();
  const [daysheet, setDaysheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(getMonday(new Date()));
  const [viewMode, setViewMode] = useState('list');
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [codes, setCodes] = useState({ treatment: [], diagnostic: [] });
  const [showAddCode, setShowAddCode] = useState(false);
  const [newCode, setNewCode] = useState({ code_type: 'treatment', code: '', description: '' });
  const [activeEntry, setActiveEntry] = useState(null); // For drag overlay
  // Undo stack — last N moves so a slipped-finger drag can be reversed.
  // Each entry: { entryId, fromDate, fromTime, toDate, toTime, childName, at }
  const [moveHistory, setMoveHistory] = useState([]);
  const [undoing, setUndoing] = useState(false);
  
  // Edit entry state
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  
  // Edit code state
  const [showEditCode, setShowEditCode] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  
  // DnD sensors — Pointer for desktop, Touch for mobile (long-press 220ms
  // with 5px tolerance so a scroll doesn't accidentally start a drag).
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 220,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  // New entry form
  const [newEntry, setNewEntry] = useState({
    child_id: '',
    child_name: '',
    time_slot: '',
    duration: 30,
    treatment_codes: [],
    diagnostic_codes: [],
    notes: ''
  });

  // Therapist's children — used by the "Add Appointment" picker so the
  // entry is linked to the actual profile (rather than free-text name).
  // Linking ensures SOAP notes auto-flip the entry to "completed" via the
  // child_id match in _auto_add_soap_to_daysheet on the backend.
  const [therapistChildren, setTherapistChildren] = useState([]);

  function getMonday(d) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff)).toISOString().split('T')[0];
  }

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  useEffect(() => {
    fetchDaysheet();
    fetchCodes();
  }, [currentWeek]);

  // Fetch the therapist's children once on mount so the "Add Appointment"
  // picker can link entries to the actual child profile (enables the
  // SOAP-→ completed-on-timetable auto-sync).
  useEffect(() => {
    const fetchChildrenForPicker = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/children`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(r.data) ? r.data : (r.data?.children || []);
        // Alphabetical by name for the picker
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTherapistChildren(list);
      } catch (e) {
        // Silent — the free-text fallback still works
        console.warn('Could not load children for daysheet picker:', e?.message || e);
      }
    };
    fetchChildrenForPicker();
  }, []);

  // Sort entries within each day chronologically by time_slot ("HH:MM").
  // Entries with no time_slot get pushed to the end. Keeps display + drag-drop
  // list + all exports (PDF / copy / share) in proper chronological order
  // without requiring a backend migration.
  const _sortEntriesByTime = (data) => {
    if (!data || !Array.isArray(data.days)) return data;
    const days = data.days.map((d) => ({
      ...d,
      entries: [...(d.entries || [])].sort((a, b) => {
        const aT = (a.time_slot || '').trim();
        const bT = (b.time_slot || '').trim();
        if (!aT && !bT) return 0;
        if (!aT) return 1;
        if (!bT) return -1;
        return aT.localeCompare(bT);
      }),
    }));
    return { ...data, days };
  };

  const fetchDaysheet = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/daysheet`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { week_start: currentWeek }
      });
      setDaysheet(_sortEntriesByTime(response.data));
      setViewMode(response.data.view_mode || 'list');
    } catch (error) {
      console.error('Failed to fetch daysheet:', error);
      toast.error('Failed to load daysheet');
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const treatment = response.data.filter(c => c.code_type === 'treatment');
      const diagnostic = response.data.filter(c => c.code_type === 'diagnostic');
      setCodes({ treatment, diagnostic });
    } catch (error) {
      console.error('Failed to fetch codes:', error);
    }
  };

  const changeWeek = (direction) => {
    const current = new Date(currentWeek);
    current.setDate(current.getDate() + (direction * 7));
    setCurrentWeek(current.toISOString().split('T')[0]);
  };

  const toggleViewMode = async (mode) => {
    setViewMode(mode);
    if (daysheet) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/admin/daysheet/${daysheet.id}/view-mode`,
          null,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { view_mode: mode }
          }
        );
      } catch (error) {
        console.error('Failed to update view mode');
      }
    }
  };

  const addEntry = async () => {
    if (!selectedDate || !newEntry.child_name) {
      toast.error('Please enter a child name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const params = {
        date: selectedDate,
        child_name: newEntry.child_name,
        time_slot: newEntry.time_slot || null,
        duration: newEntry.duration || 30,
        treatment_code: newEntry.treatment_codes.join(', '),
        diagnostic_code: newEntry.diagnostic_codes.join(', '),
        notes: newEntry.notes,
      };
      // Link to the actual child profile when picked from the dropdown.
      // This is what allows a later SOAP note to flip the entry to
      // "completed" automatically (matched by child_id on the backend).
      if (newEntry.child_id) {
        params.child_id = newEntry.child_id;
      }
      await axios.post(
        `${API}/admin/daysheet/${daysheet.id}/entry`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      toast.success('Entry added!');
      setShowAddEntry(false);
      setNewEntry({ child_id: '', child_name: '', time_slot: '', duration: 30, treatment_codes: [], diagnostic_codes: [], notes: '' });
      fetchDaysheet();
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const updateEntryStatus = async (date, entryId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/daysheet/${daysheet.id}/entry/${entryId}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status }
        }
      );
      fetchDaysheet();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteEntry = async (date, entryId, { skipConfirm = false } = {}) => {
    if (!skipConfirm && !window.confirm('Delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/admin/daysheet/${daysheet.id}/entry/${entryId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { date }
        }
      );
      toast.success('Entry deleted');
      fetchDaysheet();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const openEditEntry = (date, entry) => {
    setEditingDate(date);
    setEditingEntry({
      id: entry.id,
      child_name: entry.child_name || '',
      time_slot: entry.time_slot || '',
      duration: entry.duration || 30,
      treatment_codes: entry.treatment_code ? entry.treatment_code.split(',').map(c => c.trim()).filter(Boolean) : [],
      diagnostic_codes: entry.diagnostic_code ? entry.diagnostic_code.split(',').map(c => c.trim()).filter(Boolean) : [],
      notes: entry.notes || '',
      status: entry.status || 'scheduled'
    });
    setShowEditEntry(true);
  };

  const saveEditEntry = async () => {
    if (!editingEntry || !editingDate) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/daysheet/${daysheet.id}/entry/${editingEntry.id}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            child_name: editingEntry.child_name,
            time_slot: editingEntry.time_slot || null,
            duration: editingEntry.duration || 30,
            treatment_code: editingEntry.treatment_codes.join(', ') || null,
            diagnostic_code: editingEntry.diagnostic_codes.join(', ') || null,
            notes: editingEntry.notes || null,
            status: editingEntry.status
          }
        }
      );
      
      toast.success('Entry updated!');
      setShowEditEntry(false);
      setEditingEntry(null);
      setEditingDate(null);
      fetchDaysheet();
    } catch (error) {
      console.error('Edit error:', error);
      toast.error('Failed to update entry');
    }
  };

  const importFromSoap = async (soapNote) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/daysheet/import-soap/${daysheet.id}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { session_note_id: soapNote.id }
        }
      );
      toast.success(`Imported ${soapNote.child_name}`);
      fetchDaysheet();
    } catch (error) {
      toast.error('Failed to import');
    }
  };

  const addCode = async () => {
    if (!newCode.code) {
      toast.error('Please enter a code');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/codes`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: newCode
        }
      );
      toast.success('Code added!');
      setShowAddCode(false);
      setNewCode({ code_type: 'treatment', code: '', description: '' });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add code');
    }
  };

  const openEditCode = (code, codeType) => {
    setEditingCode({
      id: code.id,
      code: code.code,
      description: code.description || '',
      code_type: codeType
    });
    setShowEditCode(true);
  };

  const saveEditCode = async () => {
    if (!editingCode || !editingCode.code.trim()) {
      toast.error('Code is required');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/codes/${editingCode.id}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            code: editingCode.code,
            description: editingCode.description || null
          }
        }
      );
      
      toast.success('Code updated!');
      setShowEditCode(false);
      setEditingCode(null);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update code');
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm('Delete this code?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code deleted');
      fetchCodes();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const data = active.data.current;
    setActiveEntry({ ...data.entry, sourceDayDate: data.dayDate });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveEntry(null);

    if (!over || !active) return;

    // Source id format is always `<YYYY-MM-DD>-<entryId>` (DraggableEntry).
    const srcRaw = String(active.id);
    if (srcRaw.length <= 11 || srcRaw[10] !== '-') return;
    const sourceDayDate = srcRaw.substring(0, 10);
    const sourceEntryId = srcRaw.substring(11);
    const sourceEntry = active?.data?.current?.entry;
    const sourceTime = sourceEntry?.time_slot || null;

    // Target id is one of:
    //   - `slot|<date>|<HH:MM>` → time-slot drop (grid view, or list-view time row)
    //   - `<YYYY-MM-DD>-<entryId>` → dropped on another entry (sortable) — same day, no-op
    //   - `<YYYY-MM-DD>` → day zone drop (list view, no time change)
    const overRaw = String(over.id);
    let targetDayDate = null;
    let targetTime = null;
    if (overRaw.startsWith('slot|')) {
      const parts = overRaw.split('|');
      targetDayDate = parts[1];
      targetTime = parts[2];
    } else if (overRaw.length > 11 && overRaw[10] === '-') {
      targetDayDate = overRaw.substring(0, 10);
    } else {
      targetDayDate = overRaw;
    }

    // Nothing to do if both date AND time are unchanged.
    const sameDay = sourceDayDate === targetDayDate;
    const sameTime = !targetTime || targetTime === sourceTime;
    if (sameDay && sameTime) return;

    try {
      const token = localStorage.getItem('token');
      const params = { from_date: sourceDayDate, to_date: targetDayDate };
      if (targetTime) params.new_time_slot = targetTime;
      await axios.put(
        `${API}/admin/daysheet/${daysheet.id}/entry/${sourceEntryId}/move`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, params }
      );
      // Push reverse move onto the undo stack (kept at last 10).
      setMoveHistory((h) => [
        ...h.slice(-9),
        {
          entryId: sourceEntryId,
          fromDate: targetDayDate,
          fromTime: targetTime,
          toDate: sourceDayDate,
          toTime: sourceTime,
          childName: sourceEntry?.child_name || 'appointment',
          at: Date.now(),
        },
      ]);
      toast.success(
        targetTime
          ? `Moved to ${targetTime}${!sameDay ? ` on ${formatDate(targetDayDate)}` : ''}`
          : 'Entry moved!',
        {
          action: {
            label: 'Undo',
            onClick: () => undoLastMove(),
          },
        }
      );
      fetchDaysheet();
    } catch (error) {
      console.error('Move error:', error);
      toast.error('Failed to move entry');
    }
  };

  const undoLastMove = async () => {
    if (undoing) return;
    const move = moveHistory[moveHistory.length - 1];
    if (!move) {
      toast.message('Nothing to undo');
      return;
    }
    setUndoing(true);
    try {
      const token = localStorage.getItem('token');
      const params = { from_date: move.fromDate, to_date: move.toDate };
      // Pass the original time so the entry lands back exactly where it was.
      // If the source had no time, send the explicit __CLEAR__ token so the
      // backend nukes the new time slot instead of leaving it stale.
      params.new_time_slot = move.toTime || '__CLEAR__';
      await axios.put(
        `${API}/admin/daysheet/${daysheet.id}/entry/${move.entryId}/move`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, params }
      );
      setMoveHistory((h) => h.slice(0, -1));
      toast.success(`Undone — ${move.childName} is back where it was 💛`);
      fetchDaysheet();
    } catch (error) {
      toast.error("Couldn't undo — the entry may have been deleted");
    } finally {
      setUndoing(false);
    }
  };

  const handleDragCancel = () => {
    setActiveEntry(null);
  };

  const exportToPDF = () => {
    // Build print-friendly HTML content
    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Daysheet - Week of ${formatDate(currentWeek)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { font-size: 20px; margin-bottom: 20px; color: #1f2937; }
            .day-section { margin-bottom: 24px; page-break-inside: avoid; }
            .day-header { background-color: #f3f4f6; padding: 12px; font-weight: bold; border-radius: 6px; color: #374151; margin-bottom: 8px; }
            .entries { border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
            .entry { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; gap: 12px; }
            .entry:last-child { border-bottom: none; }
            .time { color: #6b7280; min-width: 50px; font-size: 14px; }
            .name { font-weight: 500; color: #1f2937; }
            .codes { color: #6b7280; font-size: 12px; }
            .status { font-size: 11px; padding: 3px 10px; border-radius: 12px; font-weight: 500; }
            .status-scheduled { background: #dbeafe; color: #1d4ed8; }
            .status-completed { background: #dcfce7; color: #15803d; }
            .status-cancelled { background: #fee2e2; color: #dc2626; }
            .status-no_show { background: #fef3c7; color: #d97706; }
            .no-entries { color: #9ca3af; padding: 16px 12px; font-style: italic; text-align: center; }
            @media print { 
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } 
              .day-section { margin-bottom: 16px; }
            }
          </style>
        </head>
        <body>
          <h1>Daysheet - Week of ${formatDate(currentWeek)}</h1>
          ${daysheet?.days?.map((day, idx) => `
            <div class="day-section">
              <div class="day-header">${DAYS_OF_WEEK[idx]} - ${formatDate(day.date)} (${day.entries?.length || 0} patients)</div>
              ${day.entries?.length > 0 ? `
                <div class="entries">
                  ${day.entries.map(entry => {
                    const codes = [];
                    if (entry.treatment_code) codes.push('T: ' + entry.treatment_code);
                    if (entry.diagnostic_code) codes.push('D: ' + entry.diagnostic_code);
                    const codeStr = codes.length > 0 ? '<span class="codes">(' + codes.join(', ') + ')</span>' : '';
                    return `
                      <div class="entry">
                        <span class="time">${entry.time_slot || '-'}</span>
                        <span class="name">${entry.child_name}</span>
                        ${codeStr}
                        <span class="status status-${entry.status}">${entry.status.replace('_', ' ')}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              ` : '<div class="entries"><div class="no-entries">No appointments scheduled</div></div>'}
            </div>
          `).join('')}
        </body>
      </html>
    `;
    
    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow || iframe.contentDocument;
    const doc = iframeDoc.document || iframeDoc;
    
    doc.open();
    doc.write(printHtml);
    doc.close();
    
    // Wait for content to load then print
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        toast.success('Print dialog opened');
      } catch (e) {
        console.error('Print error:', e);
        toast.error('Could not print. Try using Ctrl+P instead.');
      }
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Trigger onload manually for browsers that don't fire it
    setTimeout(() => {
      if (iframe.parentNode) {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          // Already handled
        }
      }
    }, 500);
  };

  const copyToClipboard = async () => {
    try {
      // Build text representation
      let text = `Daysheet - Week of ${formatDate(currentWeek)}\n\n`;
      
      daysheet?.days?.forEach((day, idx) => {
        text += `${DAYS_OF_WEEK[idx]} (${formatDate(day.date)})\n`;
        text += '-'.repeat(40) + '\n';
        
        if (day.entries?.length > 0) {
          day.entries.forEach(entry => {
            const time = entry.time_slot ? `${entry.time_slot} - ` : '';
            const codes = [];
            if (entry.treatment_code) codes.push(`T:${entry.treatment_code}`);
            if (entry.diagnostic_code) codes.push(`D:${entry.diagnostic_code}`);
            const codeStr = codes.length > 0 ? ` (${codes.join(', ')})` : '';
            text += `  ${time}${entry.child_name}${codeStr} [${entry.status}]\n`;
          });
        } else {
          text += '  No appointments\n';
        }
        text += '\n';
      });
      
      await navigator.clipboard.writeText(text);
      toast.success('Daysheet copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareViaEmail = async () => {
    // Build text content
    let textContent = `Daysheet - Week of ${formatDate(currentWeek)}\n\n`;
    
    daysheet?.days?.forEach((day, idx) => {
      textContent += `${DAYS_OF_WEEK[idx]} (${formatDate(day.date)})\n`;
      
      if (day.entries?.length > 0) {
        day.entries.forEach(entry => {
          const time = entry.time_slot ? `${entry.time_slot} - ` : '';
          const codes = [];
          if (entry.treatment_code) codes.push(`T:${entry.treatment_code}`);
          if (entry.diagnostic_code) codes.push(`D:${entry.diagnostic_code}`);
          const codeStr = codes.length > 0 ? ` (${codes.join(', ')})` : '';
          textContent += `  ${time}${entry.child_name}${codeStr}\n`;
        });
      } else {
        textContent += '  No appointments\n';
      }
      textContent += '\n';
    });
    
    // Try Web Share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Daysheet - Week of ${formatDate(currentWeek)}`,
          text: textContent
        });
        toast.success('Shared successfully!');
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Web Share failed, trying mailto...');
        } else {
          return; // User cancelled
        }
      }
    }
    
    // Fallback to mailto
    const subject = encodeURIComponent(`Daysheet - Week of ${formatDate(currentWeek)}`);
    const body = encodeURIComponent(textContent);
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    
    // Try opening mailto link
    const opened = window.open(mailtoLink, '_self');
    
    if (!opened) {
      // If mailto didn't work, copy to clipboard instead
      try {
        await navigator.clipboard.writeText(textContent);
        toast.success('Content copied! Paste it into your email.');
      } catch (clipErr) {
        toast.error('Could not share. Please use the Copy button instead.');
      }
    } else {
      toast.success('Opening email client...');
    }
  };

  const downloadAsFile = () => {
    // Build text content for download
    let textContent = `DAYSHEET - Week of ${formatDate(currentWeek)}\n`;
    textContent += '='.repeat(50) + '\n\n';
    
    daysheet?.days?.forEach((day, idx) => {
      textContent += `${DAYS_OF_WEEK[idx]} (${formatDate(day.date)}) - ${day.entries?.length || 0} patients\n`;
      textContent += '-'.repeat(40) + '\n';
      
      if (day.entries?.length > 0) {
        day.entries.forEach(entry => {
          const time = entry.time_slot ? `${entry.time_slot}` : '     ';
          const codes = [];
          if (entry.treatment_code) codes.push(`T:${entry.treatment_code}`);
          if (entry.diagnostic_code) codes.push(`D:${entry.diagnostic_code}`);
          const codeStr = codes.length > 0 ? ` [${codes.join(', ')}]` : '';
          const status = entry.status ? ` (${entry.status})` : '';
          textContent += `  ${time}  ${entry.child_name}${codeStr}${status}\n`;
        });
      } else {
        textContent += '  No appointments scheduled\n';
      }
      textContent += '\n';
    });
    
    textContent += '\n' + '='.repeat(50) + '\n';
    textContent += `Generated: ${new Date().toLocaleString()}\n`;
    
    // Create and download file
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daysheet-${currentWeek}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Daysheet downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-stone-600">Loading daysheet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] overflow-x-hidden" data-testid="admin-daysheet">
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Admin</h1>
              <p className="text-sm text-stone-500">Daysheet & Timetable</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm("Copy last week's appointments into this week? Existing entries will be kept.")) return;
                try {
                  const token = localStorage.getItem('token');
                  const r = await axios.post(
                    `${API}/admin/daysheet/copy-from-previous-week`,
                    null,
                    { headers: { Authorization: `Bearer ${token}` }, params: { week_start: currentWeek } },
                  );
                  toast.success(`Copied ${r.data?.copied_count || 0} appointments from last week`);
                  fetchDaysheet();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not copy last week.');
                }
              }}
              className="rounded-full text-stone-700 border-stone-200 hover:bg-stone-50"
              data-testid="copy-last-week-btn"
              title="Copy last week's appointments into this week"
            >
              <RotateCcw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Repeat last week</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/invoicing')}
              className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="invoicing-nav-btn"
            >
              <Receipt className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Invoicing</span>
            </Button>
            <Button variant="outline" size="sm" onClick={copyToClipboard} className="rounded-full" data-testid="copy-daysheet-btn">
              <Copy className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Copy</span>
            </Button>
            <Button variant="outline" size="sm" onClick={shareViaEmail} className="rounded-full" data-testid="share-daysheet-btn">
              <Share2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF} className="rounded-full" data-testid="print-daysheet-btn">
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Print</span>
            </Button>
            <Button variant="outline" size="sm" onClick={downloadAsFile} className="rounded-full" data-testid="download-daysheet-btn">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Download</span>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Week Navigation & View Toggle */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changeWeek(-1)} className="rounded-full">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-4 py-2 bg-white rounded-xl border border-stone-200">
              <span className="font-medium text-primary-900">
                Week of {formatDate(currentWeek)}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => changeWeek(1)} className="rounded-full">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentWeek(getMonday(new Date()))}
              className="rounded-full ml-2"
            >
              Today
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={undoLastMove}
              disabled={moveHistory.length === 0 || undoing}
              className="rounded-full"
              data-testid="undo-move-btn"
              title={moveHistory.length === 0
                ? 'No recent moves to undo'
                : `Undo: ${moveHistory[moveHistory.length - 1].childName} → back to ${moveHistory[moveHistory.length - 1].toTime || moveHistory[moveHistory.length - 1].toDate}`}
            >
              <Undo2 className="w-4 h-4 mr-1" />
              Undo
              {moveHistory.length > 0 && (
                <span className="ml-1 text-xs text-stone-500">({moveHistory.length})</span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const r = await axios.post(
                    `${API}/admin/daysheet/dedupe`,
                    null,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                      params: { week_start: currentWeek },
                    }
                  );
                  const removed = r.data?.removed || 0;
                  if (removed > 0) {
                    toast.success(`Removed ${removed} duplicate ${removed === 1 ? 'entry' : 'entries'}`);
                    fetchDaysheet();
                  } else {
                    toast.info('No duplicates found this week');
                  }
                } catch (e) {
                  toast.error('Could not dedupe daysheet');
                }
              }}
              className="rounded-full"
              data-testid="dedupe-btn"
              title="Remove scheduled entries that have been replaced by a SOAP-completed one at the same time"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Dedupe
            </Button>
            <div className="bg-stone-100 p-1 rounded-lg flex">
              <button
                onClick={() => toggleViewMode('list')}
                data-testid="view-mode-list-btn"
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1 transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm text-primary-900' : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => toggleViewMode('timetable')}
                data-testid="view-mode-timetable-btn"
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1 transition-colors ${
                  viewMode === 'timetable' ? 'bg-white shadow-sm text-primary-900' : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
                Timetable
              </button>
            </div>
          </div>
        </div>

        {/* SOAP Notes Available for Import */}
        {daysheet?.soap_notes_available?.length > 0 && (
          <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-blue-900 font-medium mb-2">SOAP Notes Available to Import</p>
                <div className="flex flex-wrap gap-2">
                  {daysheet.soap_notes_available.map(note => {
                    // Check if already imported
                    const alreadyImported = daysheet.days?.some(d => 
                      d.entries?.some(e => e.session_note_id === note.id)
                    );
                    
                    return (
                      <button
                        key={note.id}
                        onClick={() => !alreadyImported && importFromSoap(note)}
                        disabled={alreadyImported}
                        className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1 transition-colors ${
                          alreadyImported 
                            ? 'bg-stone-200 text-stone-500 cursor-not-allowed'
                            : 'bg-white text-blue-700 hover:bg-blue-100 border border-blue-200'
                        }`}
                      >
                        <Import className="w-3 h-3" />
                        {note.child_name} ({formatDate(note.date)})
                        {alreadyImported && <Check className="w-3 h-3 text-green-500" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Daysheet Content — DndContext wraps BOTH views so drag/drop
            works in list mode (move across days) AND grid mode (drop on
            an exact time slot). */}
        <div id="daysheet-content">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
          {viewMode === 'list' ? (
            // List View with Drag and Drop
            <>
              <div className="space-y-4">
                {daysheet?.days?.map((day, idx) => (
                  <Card 
                    key={day.date} 
                    className="overflow-hidden rounded-2xl border border-stone-100"
                    data-day-date={day.date}
                  >
                    <div className="bg-stone-50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-primary-900">{DAYS_OF_WEEK[idx]}</span>
                        <span className="text-sm text-stone-500">{formatDate(day.date)}</span>
                        <Badge variant="secondary" className="text-xs">
                          {day.entries?.length || 0} patients
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedDate(day.date); setShowAddEntry(true); }}
                        className="rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    
                    <SortableContext 
                      items={day.entries?.map(e => `${day.date}-${e.id}`) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <DroppableDay date={day.date}>
                        <div className={day.entries?.length > 0 ? 'divide-y divide-stone-100' : ''}>
                          {day.entries?.length > 0 ? (
                            day.entries.map(entry => (
                              <DraggableEntry
                                key={entry.id}
                                entry={entry}
                                dayDate={day.date}
                                onStatusChange={updateEntryStatus}
                                onDelete={deleteEntry}
                                onEdit={openEditEntry}
                              />
                            ))
                          ) : (
                            <div className="px-4 py-6 text-center text-stone-400 text-sm">
                              No appointments scheduled
                              <p className="text-xs mt-1 sm:hidden">
                                Tap <span className="font-medium">+ Add</span> to schedule
                              </p>
                              <p className="text-xs mt-1 hidden sm:block">
                                Drag patients here, or tap <span className="font-medium">+ Add</span>
                              </p>
                            </div>
                          )}
                        </div>
                      </DroppableDay>
                    </SortableContext>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            // Timetable View
            <div className="rounded-2xl border border-stone-100 bg-white overflow-hidden">
              <div 
                className="overflow-auto" 
                style={{ 
                  maxHeight: '70vh', 
                  maxWidth: 'calc(100vw - 2rem)',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                <table className="border-collapse" style={{ minWidth: '900px' }}>
                  <thead>
                    <tr>
                      <th 
                        className="px-3 py-3 text-left text-xs font-semibold text-stone-500 bg-stone-100 border-b border-r border-stone-200"
                        style={{ position: 'sticky', top: 0, left: 0, zIndex: 30, minWidth: '60px' }}
                      >
                        Time
                      </th>
                      {daysheet?.days?.map((day, idx) => (
                        <th 
                          key={day.date} 
                          className="px-2 py-3 text-center text-xs font-semibold text-stone-700 bg-stone-100 border-b border-stone-200"
                          style={{ position: 'sticky', top: 0, zIndex: 20, minWidth: '110px' }}
                        >
                          <div>{DAYS_OF_WEEK[idx]}</div>
                          <div className="text-stone-400 font-normal mt-0.5">{formatDate(day.date).split(', ')[1]}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.map(slot => (
                      <tr key={slot} className="border-b border-stone-50 hover:bg-stone-50/30">
                        <td 
                          className="px-3 py-2 text-xs text-stone-500 font-medium bg-white border-r border-stone-100 whitespace-nowrap"
                          style={{ position: 'sticky', left: 0, zIndex: 10 }}
                        >
                          {slot}
                        </td>
                        {daysheet?.days?.map(day => {
                          // Match entries to this slot: exact match OR closest slot for custom times
                          const entries = day.entries?.filter(e => {
                            if (!e.time_slot) return false;
                            if (e.time_slot === slot) return true;
                            // For custom times (e.g., "10:15"), find the nearest 30-min slot
                            const [eH, eM] = e.time_slot.split(':').map(Number);
                            const [sH, sM] = slot.split(':').map(Number);
                            if (eH !== sH) return false;
                            // Entry belongs to this slot if its minutes fall in this slot's range
                            return eM >= sM && eM < sM + 30;
                          }) || [];
                          return (
                            <DroppableTimeCell
                              key={`${day.date}-${slot}`}
                              as="td"
                              date={day.date}
                              time={slot}
                              className="px-1 py-1 relative min-h-12 min-w-[120px] align-top"
                            >
                              {entries.length > 0 ? (
                                <div className="space-y-1">
                                  {entries.map(entry => (
                                    <GridDraggableEntry
                                      key={entry.id}
                                      entry={entry}
                                      dayDate={day.date}
                                      slot={slot}
                                      onEdit={() => openEditEntry(day.date, entry)}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <button
                                  onClick={() => { 
                                    setSelectedDate(day.date); 
                                    setNewEntry(prev => ({ ...prev, time_slot: slot }));
                                    setShowAddEntry(true); 
                                  }}
                                  className="w-full h-full min-h-8 rounded hover:bg-stone-100 transition-colors"
                                />
                              )}
                            </DroppableTimeCell>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

            {/* Drag Overlay — shared by both list and grid views */}
            <DragOverlay>
              {activeEntry ? (
                <div className="bg-white shadow-xl rounded-lg p-3 border-2 border-blue-400 opacity-90">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-stone-400" />
                    <div>
                      <p className="font-medium text-primary-900">{activeEntry.child_name}</p>
                      {activeEntry.time_slot && (
                        <p className="text-xs text-stone-500">{activeEntry.time_slot}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Custom Codes Management */}
        <Card className="mt-6 p-5 rounded-2xl border border-stone-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-primary-900">Custom Codes</h3>
            <Button variant="outline" size="sm" onClick={() => setShowAddCode(true)} className="rounded-full">
              <Plus className="w-4 h-4 mr-1" />
              Add Code
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-stone-500 mb-2 block">Treatment Codes</Label>
              <div className="space-y-2">
                {codes.treatment.length > 0 ? codes.treatment.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-medium text-sm">{c.code}</span>
                      {c.description && <span className="text-stone-400 text-xs ml-2">- {c.description}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditCode(c, 'treatment')}
                        className="h-7 w-7 p-0 text-stone-400 hover:text-blue-500"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCode(c.id)}
                        className="h-7 w-7 p-0 text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <span className="text-xs text-stone-400">No codes saved</span>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-500 mb-2 block">Diagnostic Codes</Label>
              <div className="space-y-2">
                {codes.diagnostic.length > 0 ? codes.diagnostic.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-medium text-sm">{c.code}</span>
                      {c.description && <span className="text-stone-400 text-xs ml-2">- {c.description}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditCode(c, 'diagnostic')}
                        className="h-7 w-7 p-0 text-stone-400 hover:text-blue-500"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCode(c.id)}
                        className="h-7 w-7 p-0 text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <span className="text-xs text-stone-400">No codes saved</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      </main>

      {/* Add Entry Dialog */}
      <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Patient <span className="text-red-500">*</span></Label>
              {therapistChildren.length > 0 && (
                <Select
                  value={newEntry.child_id || ''}
                  onValueChange={(v) => {
                    if (v === '__free_text__') {
                      // Keep current free-text name; just unlink any child_id
                      setNewEntry({ ...newEntry, child_id: '' });
                      return;
                    }
                    const c = therapistChildren.find((x) => x.id === v);
                    if (c) {
                      setNewEntry({ ...newEntry, child_id: c.id, child_name: c.name || '' });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-lg h-10" data-testid="add-entry-child-picker">
                    <SelectValue placeholder="Pick from your patient list…" />
                  </SelectTrigger>
                  <SelectContent>
                    {therapistChildren.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                        {c.age ? <span className="text-stone-400 text-xs ml-2">· {c.age}</span> : null}
                      </SelectItem>
                    ))}
                    <SelectItem value="__free_text__">
                      <span className="italic text-stone-600">Other (type a name below)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Input
                value={newEntry.child_name}
                onChange={(e) =>
                  setNewEntry({ ...newEntry, child_name: e.target.value, child_id: '' })
                }
                placeholder={
                  therapistChildren.length > 0
                    ? 'Or type a name (for non-patient blocks)'
                    : 'Enter patient name'
                }
                className="rounded-lg"
                data-testid="add-entry-child-name"
              />
              {newEntry.child_id && (
                <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Linked to patient profile — completing a SOAP note will auto-mark this appointment as done.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={newEntry.time_slot}
                  onChange={(e) => setNewEntry({ ...newEntry, time_slot: e.target.value })}
                  className="rounded-lg h-10"
                  data-testid="add-entry-time"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={String(newEntry.duration || 30)}
                  onValueChange={(v) => setNewEntry({ ...newEntry, duration: parseInt(v) })}
                >
                  <SelectTrigger className="rounded-lg h-10" data-testid="add-entry-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="20">20 min</SelectItem>
                    <SelectItem value="25">25 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Treatment Codes</Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {newEntry.treatment_codes.map((code, idx) => (
                    <Badge key={idx} className="bg-purple-100 text-purple-800 pl-2 pr-1 py-1 rounded-full text-xs">
                      {code}
                      <button type="button" onClick={() => setNewEntry({
                        ...newEntry, treatment_codes: newEntry.treatment_codes.filter((_, i) => i !== idx)
                      })} className="ml-1 text-purple-500 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <Select
                  value="none"
                  onValueChange={(v) => {
                    if (v !== "none" && !newEntry.treatment_codes.includes(v)) {
                      setNewEntry({ ...newEntry, treatment_codes: [...newEntry.treatment_codes, v] });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-lg" data-testid="add-treatment-code-select">
                    <SelectValue placeholder="Select saved code..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select saved code...</SelectItem>
                    {codes.treatment.map(c => (
                      <SelectItem key={c.id} value={c.code}>{c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Type code & press Enter"
                  className="rounded-lg text-sm"
                  data-testid="add-treatment-code-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      e.preventDefault();
                      const code = e.target.value.trim().toUpperCase();
                      if (!newEntry.treatment_codes.includes(code)) {
                        setNewEntry({ ...newEntry, treatment_codes: [...newEntry.treatment_codes, code] });
                      }
                      e.target.value = '';
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Diagnostic Codes</Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {newEntry.diagnostic_codes.map((code, idx) => (
                    <Badge key={idx} className="bg-teal-100 text-teal-800 pl-2 pr-1 py-1 rounded-full text-xs">
                      {code}
                      <button type="button" onClick={() => setNewEntry({
                        ...newEntry, diagnostic_codes: newEntry.diagnostic_codes.filter((_, i) => i !== idx)
                      })} className="ml-1 text-teal-500 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <Select
                  value="none"
                  onValueChange={(v) => {
                    if (v !== "none" && !newEntry.diagnostic_codes.includes(v)) {
                      setNewEntry({ ...newEntry, diagnostic_codes: [...newEntry.diagnostic_codes, v] });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-lg" data-testid="add-diagnostic-code-select">
                    <SelectValue placeholder="Select saved code..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select saved code...</SelectItem>
                    {codes.diagnostic.map(c => (
                      <SelectItem key={c.id} value={c.code}>{c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Type code & press Enter"
                  className="rounded-lg text-sm"
                  data-testid="add-diagnostic-code-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      e.preventDefault();
                      const code = e.target.value.trim().toUpperCase();
                      if (!newEntry.diagnostic_codes.includes(code)) {
                        setNewEntry({ ...newEntry, diagnostic_codes: [...newEntry.diagnostic_codes, code] });
                      }
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                placeholder="Optional notes"
                className="rounded-lg"
              />
            </div>
          </div>
          <DialogFooter className="pb-6">
            <Button variant="outline" onClick={() => setShowAddEntry(false)}>Cancel</Button>
            <Button onClick={addEntry} className="rounded-full">Add Appointment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Code Dialog */}
      <Dialog open={showAddCode} onOpenChange={setShowAddCode}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add Custom Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code Type</Label>
              <Select
                value={newCode.code_type}
                onValueChange={(v) => setNewCode({ ...newCode, code_type: v })}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="treatment">Treatment Code</SelectItem>
                  <SelectItem value="diagnostic">Diagnostic Code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Code <span className="text-red-500">*</span></Label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                placeholder="e.g., 92507"
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newCode.description}
                onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                placeholder="e.g., Speech therapy"
                className="rounded-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCode(false)}>Cancel</Button>
            <Button onClick={addCode} className="rounded-full">Save Code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={showEditEntry} onOpenChange={setShowEditEntry}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Patient Name</Label>
                <Input
                  value={editingEntry.child_name}
                  onChange={(e) => setEditingEntry({ ...editingEntry, child_name: e.target.value })}
                  placeholder="Patient name"
                  className="rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={editingEntry.time_slot || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, time_slot: e.target.value })}
                    className="rounded-lg"
                    data-testid="edit-entry-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Select
                    value={String(editingEntry.duration || 30)}
                    onValueChange={(v) => setEditingEntry({ ...editingEntry, duration: parseInt(v) })}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="edit-entry-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="20">20 min</SelectItem>
                      <SelectItem value="25">25 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                      <SelectItem value="90">90 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Treatment Codes</Label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(editingEntry?.treatment_codes || []).map((code, idx) => (
                      <Badge key={idx} className="bg-purple-100 text-purple-800 pl-2 pr-1 py-1 rounded-full text-xs">
                        {code}
                        <button type="button" onClick={() => setEditingEntry({
                          ...editingEntry, treatment_codes: editingEntry.treatment_codes.filter((_, i) => i !== idx)
                        })} className="ml-1 text-purple-500 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <Select
                    value="none"
                    onValueChange={(v) => {
                      if (v !== "none" && !(editingEntry?.treatment_codes || []).includes(v)) {
                        setEditingEntry({ ...editingEntry, treatment_codes: [...(editingEntry?.treatment_codes || []), v] });
                      }
                    }}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="edit-treatment-code-select">
                      <SelectValue placeholder="Select saved code..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select saved code...</SelectItem>
                      {codes.treatment.map(c => (
                        <SelectItem key={c.id} value={c.code}>{c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Type code & press Enter"
                    className="rounded-lg text-sm"
                    data-testid="edit-treatment-code-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        e.preventDefault();
                        const code = e.target.value.trim().toUpperCase();
                        if (!(editingEntry?.treatment_codes || []).includes(code)) {
                          setEditingEntry({ ...editingEntry, treatment_codes: [...(editingEntry?.treatment_codes || []), code] });
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Diagnostic Codes</Label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(editingEntry?.diagnostic_codes || []).map((code, idx) => (
                      <Badge key={idx} className="bg-teal-100 text-teal-800 pl-2 pr-1 py-1 rounded-full text-xs">
                        {code}
                        <button type="button" onClick={() => setEditingEntry({
                          ...editingEntry, diagnostic_codes: editingEntry.diagnostic_codes.filter((_, i) => i !== idx)
                        })} className="ml-1 text-teal-500 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <Select
                    value="none"
                    onValueChange={(v) => {
                      if (v !== "none" && !(editingEntry?.diagnostic_codes || []).includes(v)) {
                        setEditingEntry({ ...editingEntry, diagnostic_codes: [...(editingEntry?.diagnostic_codes || []), v] });
                      }
                    }}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="edit-diagnostic-code-select">
                      <SelectValue placeholder="Select saved code..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select saved code...</SelectItem>
                      {codes.diagnostic.map(c => (
                        <SelectItem key={c.id} value={c.code}>{c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Type code & press Enter"
                    className="rounded-lg text-sm"
                    data-testid="edit-diagnostic-code-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        e.preventDefault();
                        const code = e.target.value.trim().toUpperCase();
                        if (!(editingEntry?.diagnostic_codes || []).includes(code)) {
                          setEditingEntry({ ...editingEntry, diagnostic_codes: [...(editingEntry?.diagnostic_codes || []), code] });
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editingEntry.status}
                  onValueChange={(v) => setEditingEntry({ ...editingEntry, status: v })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={editingEntry.notes}
                  onChange={(e) => setEditingEntry({ ...editingEntry, notes: e.target.value })}
                  placeholder="Optional notes"
                  className="rounded-lg"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <Button
              variant="ghost"
              onClick={async () => {
                if (!editingEntry?.id || !editingDate) return;
                if (!window.confirm(`Remove ${editingEntry.child_name || 'this appointment'} from the timetable?`)) return;
                await deleteEntry(editingDate, editingEntry.id, { skipConfirm: true });
                setShowEditEntry(false);
              }}
              className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              data-testid="edit-entry-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Remove appointment
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditEntry(false)}>Cancel</Button>
              <Button onClick={saveEditEntry} className="rounded-full">Save Changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Code Dialog */}
      <Dialog open={showEditCode} onOpenChange={setShowEditCode}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit {editingCode?.code_type === 'treatment' ? 'Treatment' : 'Diagnostic'} Code</DialogTitle>
          </DialogHeader>
          {editingCode && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Code <span className="text-red-500">*</span></Label>
                <Input
                  value={editingCode.code}
                  onChange={(e) => setEditingCode({ ...editingCode, code: e.target.value })}
                  placeholder="e.g., 92507"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingCode.description}
                  onChange={(e) => setEditingCode({ ...editingCode, description: e.target.value })}
                  placeholder="e.g., Speech therapy"
                  className="rounded-lg"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCode(false)}>Cancel</Button>
            <Button onClick={saveEditCode} className="rounded-full">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
